import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL, getWebSocketUrl } from '../../config/api';

function SOSMonitor() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState({});
  const [severityFilter, setSeverityFilter] = useState('');
  
  // Snapshots modal state
  const [activeSnapshots, setActiveSnapshots] = useState(null);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);

  // Critical incident flash state
  const [criticalFlash, setCriticalFlash] = useState(false);

  // Resolution note state
  const [resolutionTarget, setResolutionTarget] = useState(null);
  const [resolutionNote, setResolutionNote] = useState('');

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    return { 'Authorization': `Bearer ${token}` };
  }, []);

  const playAlarmSound = () => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      const audioCtx = new AudioContext();
      
      // Siren high tone
      const osc = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      osc.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(900, audioCtx.currentTime);
      gainNode.gain.setValueAtTime(0.5, audioCtx.currentTime);
      osc.start();
      
      // Siren oscillation effect
      osc.frequency.linearRampToValueAtTime(600, audioCtx.currentTime + 0.3);
      osc.frequency.linearRampToValueAtTime(900, audioCtx.currentTime + 0.6);
      osc.frequency.linearRampToValueAtTime(600, audioCtx.currentTime + 0.9);
      
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 1.2);
      osc.stop(audioCtx.currentTime + 1.2);
    } catch (e) {
      console.warn('Audio feedback blocked or failed:', e);
    }
  };

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/sos-board`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data);
        
        // Trigger alert and flashing overlay if any retrieved alert is CRITICAL and active
        const hasCritical = data.some(a => a.severity === 'CRITICAL' && a.status !== 'resolved');
        if (hasCritical) {
          setCriticalFlash(true);
          playAlarmSound();
        }
      }
    } catch (e) {
      console.error('Failed to fetch SOS board', e);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // WebSocket Live Updates
  useEffect(() => {
    fetchAlerts();

    const token = localStorage.getItem('admin_token');
    if (!token) return;

    const wsUrl = getWebSocketUrl(`/groups/ws/00000000-0000-0000-0000-000000000000?token=${token}`);

    console.log('Connecting to admin websocket:', wsUrl);
    const socket = new WebSocket(wsUrl);

    socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log('WS Alert Message received:', message);
        
        if (message.type === 'sos_alert') {
          const newAlert = message.data;
          
          setAlerts(prev => {
            if (prev.some(a => a.id === newAlert.id)) return prev;
            
            // Check for critical severity alert triggers
            if (newAlert.severity === 'CRITICAL') {
              setCriticalFlash(true);
              playAlarmSound();
            }
            return [newAlert, ...prev];
          });
        } else if (message.type === 'sos_update') {
          const { sos_id, status, severity } = message.data;
          
          // Remove if resolved or cancelled
          if (status === 'resolved' || status === 'cancelled') {
            setAlerts(prev => prev.filter(a => a.id !== sos_id));
          } else {
            setAlerts(prev => prev.map(a => {
              if (a.id === sos_id) {
                const updated = {
                  ...a,
                  ...(status ? { status } : {}),
                  ...(severity ? { severity } : {})
                };
                if (severity === 'CRITICAL' && status !== 'resolved') {
                  setCriticalFlash(true);
                  playAlarmSound();
                }
                return updated;
              }
              return a;
            }));
          }
        }
      } catch (err) {
        console.error('Failed to parse WebSocket message', err);
      }
    };

    socket.onerror = (e) => {
      console.error('WebSocket connection error:', e);
    };

    socket.onclose = () => {
      console.log('WebSocket closed. Reconnecting disabled for this session.');
    };

    return () => {
      socket.close();
    };
  }, [fetchAlerts]);

  const updateStatus = async (sosId, status) => {
    setResolving(prev => ({ ...prev, [sosId]: true }));
    try {
      const url = `${API_BASE_URL}/admin/sos/${sosId}/${status}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error(`Failed to set status ${status}`);
      
      alert(`SOS Alert marked as ${status}`);
      fetchAlerts();
    } catch (e) {
      console.error(e);
      alert(`Failed to update status to ${status}`);
    } finally {
      setResolving(prev => ({ ...prev, [sosId]: false }));
    }
  };

  const handleResolveAlertSubmit = async () => {
    if (!resolutionTarget) return;
    const sosId = resolutionTarget;
    setResolving(prev => ({ ...prev, [sosId]: true }));
    try {
      const res = await fetch(`${API_BASE_URL}/admin/sos/${sosId}/resolve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ resolution_note: resolutionNote })
      });
      if (!res.ok) throw new Error('Failed to resolve alert');
      
      alert('SOS Alert marked as RESOLVED');
      setResolutionTarget(null);
      setResolutionNote('');
      fetchAlerts();
    } catch (e) {
      console.error(e);
      alert('Failed to resolve SOS Alert');
    } finally {
      setResolving(prev => ({ ...prev, [sosId]: false }));
    }
  };

  const changeSeverity = async (sosId, newSeverity) => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/sos/${sosId}/severity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ severity: newSeverity })
      });
      if (!res.ok) throw new Error('Failed to update severity');
      
      alert(`Severity level adjusted to ${newSeverity}`);
      if (newSeverity === 'CRITICAL') {
        setCriticalFlash(true);
        playAlarmSound();
      }
      fetchAlerts();
    } catch (e) {
      console.error(e);
      alert('Failed to change severity level');
    }
  };

  const viewSnapshots = async (sosId) => {
    setLoadingSnapshots(true);
    setActiveSnapshots([]);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/sos/${sosId}/snapshots`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        setActiveSnapshots(await res.json());
      }
    } catch (e) {
      console.error('Failed to load snapshots', e);
      alert('Failed to load telemetry snapshots');
    } finally {
      setLoadingSnapshots(false);
    }
  };

  const filteredAlerts = alerts.filter(a => {
    if (severityFilter && a.severity !== severityFilter) return false;
    return true;
  });

  const getSeverityBadgeClass = (sev) => {
    switch (sev) {
      case 'CRITICAL': return 'badge-danger';
      case 'HIGH': return 'badge-danger';
      case 'MEDIUM': return 'badge-success';
      case 'LOW': return 'badge-info';
      default: return 'badge-info';
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', position: 'relative' }}>
      
      {/* Flashing critical warning overlay */}
      {criticalFlash && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          border: '10px solid red',
          pointerEvents: 'none',
          zIndex: 9999,
          animation: 'flash-border 1s infinite alternate'
        }}>
          <div style={{
            background: 'rgba(255,0,0,0.1)',
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            paddingTop: '20px'
          }}>
            <div style={{
              background: 'red',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              fontWeight: 'bold',
              fontSize: '18px',
              pointerEvents: 'auto',
              cursor: 'pointer',
              boxShadow: '0 4px 10px rgba(0,0,0,0.3)'
            }} onClick={() => setCriticalFlash(false)}>
              ⚠️ ACTIVE CRITICAL DISTRESS ALERTS DETECTED (Click to dismiss flash)
            </div>
          </div>
        </div>
      )}

      {/* Styled animation block for border flash */}
      <style>{`
        @keyframes flash-border {
          from { border-color: red; }
          to { border-color: transparent; }
        }
      `}</style>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
          <h3>SOS Operations Center</h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
            >
              <option value="">All Severities</option>
              <option value="CRITICAL">CRITICAL</option>
              <option value="HIGH">HIGH</option>
              <option value="MEDIUM">MEDIUM</option>
              <option value="LOW">LOW</option>
            </select>
            <button className="btn-outline" onClick={fetchAlerts}>Refresh Feed</button>
          </div>
        </div>

        {loading ? (
          <p>Loading active SOS alerts...</p>
        ) : filteredAlerts.length === 0 ? (
          <p style={{ color: 'green', fontWeight: 'bold' }}>✅ No active SOS distress alerts. All routes clear!</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>User / Phone</th>
                <th>Severity</th>
                <th>Status</th>
                <th>Distress Note</th>
                <th>Coordinates</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAlerts.map((alert) => (
                <tr key={alert.id} style={{ backgroundColor: alert.severity === 'CRITICAL' ? 'rgba(255, 0, 0, 0.05)' : 'transparent' }}>
                  <td>
                    <strong>{alert.user_name}</strong>
                    <div style={{ fontSize: '12px', color: '#666' }}>{alert.mobile || 'No Mobile'}</div>
                  </td>
                  <td>
                    <select
                      value={alert.severity}
                      onChange={(e) => changeSeverity(alert.id, e.target.value)}
                      style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '12px', marginRight: '6px' }}
                    >
                      <option value="LOW">LOW</option>
                      <option value="MEDIUM">MEDIUM</option>
                      <option value="HIGH">HIGH</option>
                      <option value="CRITICAL">CRITICAL</option>
                    </select>
                    <span className={getSeverityBadgeClass(alert.severity)}>
                      {alert.severity}
                    </span>
                  </td>
                  <td>
                    <span className="badge-danger">{alert.status?.toUpperCase()}</span>
                  </td>
                  <td style={{ maxWidth: '200px' }}>{alert.note || '—'}</td>
                  <td>
                    {alert.latitude?.toFixed(5)}, {alert.longitude?.toFixed(5)}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        className="btn-outline"
                        style={{ padding: '4px 8px', fontSize: '12px' }}
                        onClick={() => viewSnapshots(alert.id)}
                      >
                        Telemetry
                      </button>
                      
                      {alert.status === 'active' && (
                        <button
                          className="btn-outline"
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                          disabled={resolving[alert.id]}
                          onClick={() => updateStatus(alert.id, 'acknowledge')}
                        >
                          Ack
                        </button>
                      )}

                      {alert.status !== 'escalated' && (
                        <button
                          className="btn-outline"
                          style={{ padding: '4px 8px', fontSize: '12px', borderColor: 'orange', color: 'orange' }}
                          disabled={resolving[alert.id]}
                          onClick={() => updateStatus(alert.id, 'escalate')}
                        >
                          Escalate
                        </button>
                      )}

                      <button
                        className="btn-primary"
                        style={{ padding: '4px 8px', fontSize: '12px' }}
                        disabled={resolving[alert.id]}
                        onClick={() => setResolutionTarget(alert.id)}
                      >
                        Resolve
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Telemetry Snapshots Overlay Panel */}
      {activeSnapshots && (
        <div className="card" style={{ border: '2px solid var(--secondary)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4>Group Spatial Telemetry Snapshots (Exact Millisecond)</h4>
            <button className="btn-outline" onClick={() => setActiveSnapshots(null)}>Close Overlay</button>
          </div>
          
          {loadingSnapshots ? (
            <p style={{ marginTop: '12px' }}>Querying Redis telemetry snapshots...</p>
          ) : activeSnapshots.length === 0 ? (
            <p style={{ marginTop: '12px', color: '#666' }}>No other group members registered coordinates during this distress trigger.</p>
          ) : (
            <table className="admin-table" style={{ marginTop: '12px' }}>
              <thead>
                <tr>
                  <th>Member Name</th>
                  <th>Member ID</th>
                  <th>Coordinates (Lat/Lon)</th>
                  <th>Captured Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {activeSnapshots.map((snap) => (
                  <tr key={snap.id}>
                    <td><strong>{snap.user_name}</strong></td>
                    <td style={{ fontSize: '12px', fontFamily: 'monospace' }}>{snap.user_id}</td>
                    <td>{snap.latitude?.toFixed(5)}, {snap.longitude?.toFixed(5)}</td>
                    <td>{new Date(snap.captured_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Resolve SOS modal target */}
      {resolutionTarget && (
        <div className="card" style={{ border: '2px solid green' }}>
          <h4>Resolve SOS Alert</h4>
          <p style={{ fontSize: '12px', color: '#666' }}>Target Alert ID: {resolutionTarget}</p>
          <input
            type="text"
            placeholder="Describe resolution action taken (medical dispatched, volunteer helped, etc.)..."
            value={resolutionNote}
            onChange={(e) => setResolutionNote(e.target.value)}
            style={{ width: '100%', padding: '10px', marginTop: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
          />
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
            <button className="btn-primary" onClick={handleResolveAlertSubmit}>Submit Resolution</button>
            <button className="btn-outline" onClick={() => setResolutionTarget(null)}>Cancel</button>
          </div>
        </div>
      )}

    </div>
  );
}

export default SOSMonitor;
